import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, Globe } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import Button from '../components/common/Button';
import { ReactComponent as RodBotLogo } from '../assets/images/rodbotsvg.svg';
import './LoginPage.css';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { currentLanguage, changeLanguage, availableLanguages, isRTL } = useLanguage();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Redirect if already authenticated — go to first enabled page (not hardcoded)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/');  // App.jsx root route handles redirecting to first enabled feature
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('auth.loginError'));
      return;
    }

    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success(t('auth.loginSuccess'));
        navigate('/');  // App.jsx root route handles redirecting to first enabled feature
      } else {
        toast.error(result.error || t('auth.loginError'));
      }
    } catch (error) {
      toast.error(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const currentLang = availableLanguages.find(l => l.code === currentLanguage);

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`login-page ${isRTL ? 'rtl' : ''}`}>
      {/* Language Selector */}
      <div className="login-lang-selector">
        <button 
          className="lang-button"
          onClick={() => setShowLangMenu(!showLangMenu)}
        >
          <Globe size={18} />
          <span>{currentLang?.nativeName}</span>
        </button>
        
        {showLangMenu && (
          <div className="lang-menu">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                className={`lang-item ${lang.code === currentLanguage ? 'active' : ''}`}
                onClick={() => {
                  changeLanguage(lang.code);
                  setShowLangMenu(false);
                }}
              >
                {lang.nativeName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Background decoration */}
      <div className="login-bg">
        <div className="login-bg-gradient"></div>
        <div className="login-bg-pattern"></div>
      </div>

      {/* Login Card */}
      <div className="login-container">
        <div className="login-card">
          {/* Logo */}
          <div className="login-header">
            <div className="login-logo">
              <RodBotLogo className="login-logo-svg" />
            </div>
            <h1 className="login-title">RodBotAI</h1>
            <p className="login-subtitle">{t('auth.enterCredentials')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t('auth.email')}
              </label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  type="text"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email')}
                  className="form-input"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t('auth.password')}
              </label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password')}
                  className="form-input"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="login-button"
            >
              {loading ? t('auth.loggingIn') : t('auth.loginButton')}
            </Button>
          </form>

          <p className="auth-switch">
            {t('auth.noAccount', "Don't have an account?")}{' '}
            <Link to="/register" className="auth-switch-link">{t('auth.register', 'Register')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

